<template>
  <DashboardLayout>
    <template #header>
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <Link :href="route('settings.roles.index')">
            <Button variant="ghost" size="sm">
              <ArrowLeftIcon class="w-4 h-4 mr-2" />
              Back to Roles
            </Button>
          </Link>
          <div>
            <div class="flex items-center space-x-3">
              <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {{ role.name }}
              </h1>
              <Badge :variant="role.is_system ? 'secondary' : 'primary'" size="md">
                {{ role.is_system ? 'System Role' : 'Custom Role' }}
              </Badge>
            </div>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
              {{ role.description || 'No description provided' }}
            </p>
          </div>
        </div>

        <div v-if="!role.is_system && canManageRoles" class="flex space-x-2">
          <Link :href="route('settings.roles.edit', role.id)">
            <Button variant="primary" size="md">
              <PencilIcon class="w-4 h-4 mr-2" />
              Edit Role
            </Button>
          </Link>
          <Button
            v-if="users.length === 0"
            variant="danger"
            size="md"
            @click="confirmDelete"
          >
            <TrashIcon class="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>
    </template>

    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                Permissions
              </p>
              <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ role.permissions_count }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Assigned to this role
              </p>
            </div>
            <div class="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <KeyIcon class="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                Users
              </p>
              <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ users.length }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                With this role
              </p>
            </div>
            <div class="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <UsersIcon class="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                Modules
              </p>
              <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ moduleCount }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Across modules
              </p>
            </div>
            <div class="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CubeIcon class="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
      </div>

      <!-- Tabs -->
      <Card>
        <div class="border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
          <nav class="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              @click="activeTab = 'permissions'"
              :class="[
                activeTab === 'permissions'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors'
              ]"
            >
              <KeyIcon class="w-5 h-5 inline-block mr-2" />
              Permissions ({{ role.permissions_count }})
            </button>
            <button
              @click="activeTab = 'users'"
              :class="[
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors'
              ]"
            >
              <UsersIcon class="w-5 h-5 inline-block mr-2" />
              Users ({{ users.length }})
            </button>
          </nav>
        </div>

        <!-- Permissions Tab Content -->
        <div v-show="activeTab === 'permissions'" class="p-6">
          <div v-if="role.permissions_count === 0" class="text-center py-12">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
              <ShieldExclamationIcon class="h-8 w-8 text-gray-400 dark:text-gray-600" />
            </div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No permissions assigned
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-6">
              This role doesn't have any permissions yet.
            </p>
            <Link v-if="!role.is_system && canManageRoles" :href="route('settings.roles.edit', role.id)">
              <Button variant="primary">
                <PencilIcon class="w-4 h-4 mr-2" />
                Edit Role
              </Button>
            </Link>
          </div>

          <div v-else class="space-y-6">
            <!-- Search/Filter Bar -->
            <div class="flex items-center justify-between">
              <div class="relative flex-1 max-w-md">
                <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  v-model="permissionSearch"
                  type="text"
                  placeholder="Search permissions..."
                  class="pl-10 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100"
                />
              </div>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  {{ filteredPermissionsCount }} permissions
                </span>
              </div>
            </div>

            <!-- Permissions by Module -->
            <div class="space-y-4">
              <div
                v-for="(moduleData, moduleName) in filteredPermissionsByModule"
                :key="moduleName"
                class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <div class="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <div class="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <CubeIcon class="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {{ formatModuleName(moduleName) }}
                        </h3>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ moduleData.permissions.length }} permissions
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" size="sm">
                      {{ moduleData.permissions.length }}
                    </Badge>
                  </div>
                </div>

                <div class="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                  <div
                    v-for="permission in moduleData.permissions"
                    :key="permission.id"
                    class="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center space-x-3 flex-1">
                        <div class="flex-shrink-0">
                          <div class="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <KeyIcon class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {{ permission.name }}
                          </p>
                          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {{ permission.description || 'No description' }}
                          </p>
                          <div class="flex items-center space-x-2 mt-1">
                            <Badge variant="info" size="xs">
                              {{ permission.category || 'General' }}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Results -->
            <div v-if="filteredPermissionsCount === 0 && permissionSearch" class="text-center py-12">
              <MagnifyingGlassIcon class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                No permissions found
              </h3>
              <p class="text-gray-600 dark:text-gray-400">
                Try adjusting your search terms
              </p>
            </div>
          </div>
        </div>

        <!-- Users Tab Content -->
        <div v-show="activeTab === 'users'" class="p-6">
          <div v-if="users.length === 0" class="text-center py-12">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
              <UsersIcon class="h-8 w-8 text-gray-400 dark:text-gray-600" />
            </div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No users assigned
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-6">
              No users currently have this role.
            </p>
            <Link :href="route('team.index')">
              <Button variant="primary">
                <UserPlusIcon class="w-4 h-4 mr-2" />
                Manage Team
              </Button>
            </Link>
          </div>

          <div v-else class="space-y-4">
            <!-- Search Bar -->
            <div class="relative max-w-md">
              <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                v-model="userSearch"
                type="text"
                placeholder="Search users..."
                class="pl-10 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100"
              />
            </div>

            <!-- Users List -->
            <div class="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div
                v-for="user in filteredUsers"
                :key="user.id"
                class="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-4">
                    <!-- Avatar -->
                    <div class="flex-shrink-0">
                      <div v-if="user.profile_photo" class="w-12 h-12 rounded-full overflow-hidden">
                        <img :src="user.profile_photo" :alt="user.full_name" class="w-full h-full object-cover" />
                      </div>
                      <div v-else class="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <span class="text-lg font-semibold text-primary-600 dark:text-primary-400">
                          {{ getUserInitials(user.full_name) }}
                        </span>
                      </div>
                    </div>

                    <!-- User Info -->
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {{ user.full_name }}
                      </p>
                      <p class="text-sm text-gray-500 dark:text-gray-400">
                        {{ user.email }}
                      </p>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="flex items-center space-x-2">
                    <Link :href="route('profile.show', { user: user.id })">
                      <Button variant="ghost" size="sm">
                        <EyeIcon class="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Results -->
            <div v-if="filteredUsers.length === 0 && userSearch" class="text-center py-12">
              <MagnifyingGlassIcon class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                No users found
              </h3>
              <p class="text-gray-600 dark:text-gray-400">
                Try adjusting your search terms
              </p>
            </div>
          </div>
        </div>
      </Card>

      <!-- Role Details Card -->
      <Card>
        <template #header>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
            Role Information
          </h2>
        </template>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
              Role Name
            </label>
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {{ role.name }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
              Slug
            </label>
            <p class="text-sm text-gray-900 dark:text-gray-100 font-mono">
              {{ role.slug }}
            </p>
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
              Description
            </label>
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {{ role.description || 'No description provided' }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
              Type
            </label>
            <Badge :variant="role.is_system ? 'secondary' : 'primary'" size="sm">
              {{ role.is_system ? 'System Role' : 'Custom Role' }}
            </Badge>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
              Created
            </label>
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {{ formatDate(role.created_at) }}
            </p>
          </div>
        </div>
      </Card>
    </div>

    <!-- Delete Confirmation Modal -->
    <Modal :show="showDeleteModal" @close="showDeleteModal = false">
      <template #title>
        Delete Role
      </template>

      <template #content>
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <ExclamationTriangleIcon class="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Delete "{{ role.name }}"?
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            This action cannot be undone. This will permanently delete the role and all its permission assignments.
          </p>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <Button variant="secondary" @click="showDeleteModal = false">
            Cancel
          </Button>
          <Button variant="danger" @click="deleteRole">
            <TrashIcon class="w-4 h-4 mr-2" />
            Delete Role
          </Button>
        </div>
      </template>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UsersIcon,
  CubeIcon,
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  EyeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/vue/24/outline';
import { useAuth } from '@/Composables/useAuth';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  role: {
    type: Object,
    required: true,
  },
  users: {
    type: Array,
    default: () => [],
  },
});

const { can } = useAuth();
const { success, error: showError } = useToast();

// Permission check
const canManageRoles = computed(() => can('system.roles.manage'));

// Active tab
const activeTab = ref('permissions');

// Search filters
const permissionSearch = ref('');
const userSearch = ref('');

// Delete modal
const showDeleteModal = ref(false);

// Module count
const moduleCount = computed(() => {
  return Object.keys(props.role.permissions_by_module || {}).length;
});

// Filtered permissions by module
const filteredPermissionsByModule = computed(() => {
  if (!permissionSearch.value) {
    return props.role.permissions_by_module || {};
  }

  const search = permissionSearch.value.toLowerCase();
  const filtered = {};

  Object.entries(props.role.permissions_by_module || {}).forEach(([module, data]) => {
    const filteredPermissions = data.permissions.filter(permission =>
      permission.name.toLowerCase().includes(search) ||
      permission.description?.toLowerCase().includes(search) ||
      permission.category?.toLowerCase().includes(search)
    );

    if (filteredPermissions.length > 0) {
      filtered[module] = {
        ...data,
        permissions: filteredPermissions,
      };
    }
  });

  return filtered;
});

// Filtered permissions count
const filteredPermissionsCount = computed(() => {
  let count = 0;
  Object.values(filteredPermissionsByModule.value).forEach(moduleData => {
    count += moduleData.permissions.length;
  });
  return count;
});

// Filtered users
const filteredUsers = computed(() => {
  if (!userSearch.value) {
    return props.users;
  }

  const search = userSearch.value.toLowerCase();
  return props.users.filter(user =>
    user.full_name.toLowerCase().includes(search) ||
    user.email.toLowerCase().includes(search)
  );
});

/**
 * Format module name for display
 */
const formatModuleName = (name) => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Get user initials
 */
const getUserInitials = (name) => {
  if (!name) return '?';

  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Confirm delete
 */
const confirmDelete = () => {
  showDeleteModal.value = true;
};

/**
 * Delete role
 */
const deleteRole = () => {
  router.delete(route('settings.roles.destroy', props.role.id), {
    onSuccess: () => {
      success('Role deleted successfully.');
    },
    onError: (errors) => {
      showError(errors.message || 'Failed to delete role. Please try again.');
    },
  });
};
</script>
